/**
 * Provides secure, maintainable cookie-based storage for authentication tokens
 */

class CookieService {
  constructor() {
    // Use a unique cookie name to avoid collisions with server/proxy cookies.
    // Keep backward compatibility by reading the old key if present.
    this.tokenKey = 'strack-jwt-token';
    this.legacyTokenKey = 'jwt-token-storage';
    this.cookieOptions = {
      secure: process.env.NODE_ENV === 'production', // HTTPS only in production
      sameSite: 'lax', // Changed from 'strict' to 'lax' for better compatibility
      path: '/', // Available across the entire site
      maxAge: 24 * 60 * 60, // 24 hours in seconds (browser expects seconds, not milliseconds)
    };
  }

  /**
   * Set a cookie with the given name and value
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {Object} options - Cookie options (optional)
   */
  setCookie(name, value, options = {}) {
    const cookieOptions = { ...this.cookieOptions, ...options };
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    // Max-Age is in seconds for cookies
    if (cookieOptions.maxAge) {
      cookieString += `; Max-Age=${cookieOptions.maxAge}`;
    }
    if (cookieOptions.path) {
      cookieString += `; Path=${cookieOptions.path}`;
    }
    if (cookieOptions.domain) {
      cookieString += `; Domain=${cookieOptions.domain}`;
    }
    // Only set Secure flag in production (HTTPS)
    if (cookieOptions.secure && process.env.NODE_ENV === 'production') {
      cookieString += '; Secure';
    }
    if (cookieOptions.sameSite) {
      cookieString += `; SameSite=${cookieOptions.sameSite}`;
    }
    // Note: HttpOnly cannot be set via JavaScript for security reasons

    document.cookie = cookieString;
    console.log('CookieService: Set cookie', name, 'with options:', cookieOptions);
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} - Cookie value or null if not found
   */
  getCookie(name) {
    const nameEQ = encodeURIComponent(name) + "=";
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
        console.log('CookieService: Retrieved cookie', name, 'exists:', !!value);
        return value;
      }
    }
    console.log('CookieService: Cookie', name, 'not found. All cookies:', document.cookie);
    return null;
  }

  /**
   * Remove a cookie by name
   * @param {string} name - Cookie name
   * @param {Object} options - Cookie options (optional)
   */
  removeCookie(name, options = {}) {
    const cookieOptions = { ...this.cookieOptions, ...options };
    // Set expiration to past date to remove cookie
    cookieOptions.maxAge = -1;
    this.setCookie(name, '', cookieOptions);
  }

  /**
   * Check if a cookie exists
   * @param {string} name - Cookie name
   * @returns {boolean} - True if cookie exists
   */
  hasCookie(name) {
    return this.getCookie(name) !== null;
  }

  /**
   * Clear all cookies (for logout)
   */
  clearAllCookies() {
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      this.removeCookie(name);
    }
  }

  // Token-specific methods for backward compatibility
  /**
   * Set JWT token in secure cookie
   * @param {string} token - JWT token
   */
  setToken(token) {
    console.log('CookieService: Setting token, length:', token ? token.length : 0);
    this.setCookie(this.tokenKey, token);
    // Verify it was set
    const verify = this.getToken();
    console.log('CookieService: Token set verification:', verify ? 'SUCCESS' : 'FAILED');
  }

  /**
   * Get JWT token from secure cookie
   * @returns {string|null} - JWT token or null if not found
   */
  getToken() {
    return this.getCookie(this.tokenKey) || this.getCookie(this.legacyTokenKey);
  }

  /**
   * Remove JWT token from secure cookie
   */
  removeToken() {
    console.log('CookieService: Removing token');
    this.removeCookie(this.tokenKey);
    this.removeCookie(this.legacyTokenKey);
  }

  /**
   * Check if JWT token exists
   * @returns {boolean} - True if token exists
   */
  hasToken() {
    return this.hasCookie(this.tokenKey);
  }

  setItem(key, value) {
    this.setCookie(key, value);
  }

  getItem(key) {
    return this.getCookie(key);
  }

  removeItem(key) {
    this.removeCookie(key);
  }

  clear() {
    this.clearAllCookies();
  }
}

// Create singleton instance
const cookieService = new CookieService();

export default cookieService; 